/* eslint-disable no-console */
import React, { ReactNode } from 'react';
import { useTranslation } from 'next-i18next';
import { ErrorBoundary, isChunkLoadError, reportChunkLoadFailure } from '@restworld/utils-common';

import { Button, Heading } from '@restworld/ui-ds-base';

/** When the last automatic reload happened, so a file that stays unreachable cannot loop the page. */
const AUTO_RELOAD_AT_KEY = 'rw:chunk-load-auto-reload-at';
const AUTO_RELOAD_WINDOW_MS = 60_000;
/** Time for the boundary's RUM error and the `chunk_load_failure` log to leave before the reload. */
const AUTO_RELOAD_DELAY_MS = 1500;

interface ErrorBoundaryProps {
  children: ReactNode;
}
export const ErrorBoundaryWrapper: React.FC<ErrorBoundaryProps> = ({ children }) => {
  // Read here, before anything can crash: the fallback renders after a failure, and `common` is
  // loaded by every page that ships translations, so the crash screen cannot show a raw key.
  const { t } = useTranslation('common');

  return (
    <ErrorBoundary
      onError={(error, componentStack) => {
        // The boundary already reports the error to OpenObserve RUM; this is just local logging.
        console.trace(error, 'page error');
        console.trace(componentStack, 'page error componentStack');

        // A screen whose code still failed to download after the in-page retries of
        // withChunkLoadRetry: reload once, which brings the visitor back to the same place (the
        // onboarding wizards keep their state in browser storage). Not more than once a minute: on
        // 18/10/2026 one file stayed unreachable for about a minute, and the button below covers
        // that case. Without sessionStorage there is no way to stop a loop, so no automatic reload.
        if (!isChunkLoadError(error)) return;
        const autoReload = () => {
          try {
            const lastReloadAt = Number(window.sessionStorage.getItem(AUTO_RELOAD_AT_KEY)) || 0;
            if (Date.now() - lastReloadAt < AUTO_RELOAD_WINDOW_MS) return;
            window.sessionStorage.setItem(AUTO_RELOAD_AT_KEY, String(Date.now()));
          } catch {
            return;
          }
          void reportChunkLoadFailure(error, { outcome: 'auto_reload' });
          window.setTimeout(() => window.location.reload(), AUTO_RELOAD_DELAY_MS);
        };
        // Offline, a reload would swap this screen for the browser's own "no internet" page, which
        // has no way back. Keep this screen and reload once the connection is back.
        if (!navigator.onLine) {
          window.addEventListener('online', autoReload, { once: true });
          return;
        }
        autoReload();
      }}
      fallback={
        <div className="fixed inset-0 flex flex-col items-center justify-center gap-6 p-8 text-center">
          <Heading size={1} weight="bold" className="max-w-2xl">
            {t('unexpected_error_contact_support')}
          </Heading>
          <Button variant="primary" onClick={() => window.location.reload()}>
            {t('reload_page')}
          </Button>
        </div>
      }
    >
      {children}
    </ErrorBoundary>
  );
};
export default ErrorBoundaryWrapper;
